import { diagnosticLog } from './diagnosticLogger';

/** Parse install/signature failure payload from maestro://update-error. */
export function parseUpdateErrorPayload(payload) {
  if (!payload) return { message: 'Update failed', remoteVersion: '' };
  if (typeof payload === 'string') return { message: payload, remoteVersion: '' };
  return {
    message: payload.message || 'Update failed',
    remoteVersion: payload.remote_version || payload.remoteVersion || '',
  };
}

/** Parse check-failure payload from maestro://update-check-failed (or legacy alias). */
export function parseUpdateCheckFailedPayload(payload) {
  if (typeof payload === 'string' && payload.trim()) return payload.trim();
  if (payload && typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message.trim();
  }
  return "Couldn't check for updates — will retry.";
}

/** Record updater failures to Connector fleet JSONL via diagnostic_log_js. */
export function logUpdaterDiagnostic(kind, message, extra) {
  try {
    diagnosticLog('error', 'updater', message, {
      updater_kind: kind,
      ...(extra || {}),
    });
  } catch {
    /* never propagate */
  }
}

/**
 * v11.0.71 check_for_updates_manual returns a diagnostic string instead of emitting events.
 * Parse it so Settings does not stay stuck on "checking".
 */
export function parseLegacyManualCheckResult(result) {
  const text = String(result || '').trim();
  if (!text) {
    return { status: 'check-failed', message: 'Update check returned no response.' };
  }
  if (/up to date|No update available/i.test(text)) {
    return { status: 'up-to-date', message: text };
  }
  const versionMatch = text.match(/→\s*([\d.]+)\s*available/i);
  if (/available!/i.test(text) || versionMatch) {
    return {
      status: 'update-available',
      message: text,
      version: versionMatch?.[1] || null,
    };
  }
  if (/FAILED|TIMEOUT|error/i.test(text)) {
    return { status: 'check-failed', message: text };
  }
  return { status: 'check-failed', message: text };
}

export function manualDownloadUrl(remoteVersion) {
  const ua = (navigator.userAgent || '').toLowerCase();
  const isWin = ua.includes('win');
  const base = 'https://api.maestroaudio.net/api/maestro/updates/download';
  if (isWin) {
    const ver = remoteVersion || 'latest';
    return `${base}/Maestro_${ver}_x64-setup.exe`;
  }
  return `${base}/Maestro_aarch64.dmg`;
}

export async function openManualDownloadUrl(url) {
  try {
    const core = window.__TAURI__?.core || window.__TAURI__;
    if (typeof core?.invoke === 'function') {
      await core.invoke('plugin:shell|open', { path: url });
      return;
    }
  } catch (e) {
    console.error('[Update] shell.open failed:', e);
  }
  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    console.error('[Update] anchor-click fallback failed:', e);
  }
}

export async function retryConnectorUpdateCheck() {
  try {
    const { triggerConnectorUpdateCheck } = await import('../lib/connector');
    return triggerConnectorUpdateCheck();
  } catch (e) {
    console.error('[Update] retry check failed:', e);
    throw e;
  }
}

/**
 * Pre-v11.0.221 Connectors: no maestro://update-* events — fetch published tag and compare.
 */
export async function runLegacyConnectorUpdateCheckFallback(installedVersion) {
  const { fetchConnectorLatestPublishedVersion } = await import('./connectorLatestVersion');
  const { connectorMeetsMinVersion, parseConnectorVersion } = await import('./connectorVersion');
  const latest = await fetchConnectorLatestPublishedVersion();
  const installed = String(installedVersion || '').replace(/^v/i, '').trim();
  if (!latest) {
    return { status: 'check-failed', message: 'Could not reach the update server.' };
  }
  if (!installed || !parseConnectorVersion(installed)) {
    return {
      status: 'update-available',
      message: `v${latest} is available.`,
      version: latest,
      downloadUrl: manualDownloadUrl(latest),
    };
  }
  if (installed === latest || connectorMeetsMinVersion(installed, latest)) {
    return { status: 'up-to-date', message: `You're on the latest version (v${installed}).`, version: installed };
  }
  return {
    status: 'update-available',
    message: `v${latest} is available.`,
    version: latest,
    downloadUrl: manualDownloadUrl(latest),
  };
}

/** Parse maestro://update-available payload (string version or { version, downloadUrl }). */
export function parseUpdateAvailablePayload(payload) {
  if (!payload) return { version: '', downloadUrl: '' };
  if (typeof payload === 'string') {
    return { version: payload.replace(/^v/i, ''), downloadUrl: manualDownloadUrl(payload) };
  }
  const version = String(payload.version || payload.remote_version || '').replace(/^v/i, '');
  const downloadUrl = String(payload.downloadUrl || payload.download_url || '').trim()
    || (version ? manualDownloadUrl(version) : '');
  return { version, downloadUrl };
}
